/**
 * sessionStorage cache for the PermissionSnapshot, keyed per organization.
 *
 * sessionStorage outlives logout/login within a tab, so an unkeyed entry written
 * in org A is served as org B's roles on the next sign-in until the background
 * fetch lands — and for good if that fetch fails (a silent refresh keeps the stale
 * snapshot). Keying by org makes a cross-org read impossible rather than brief.
 * Ref: ASC-10848
 */

import type { PermissionSnapshot } from '@platform/accesszen-contracts'
import { PermissionSnapshotSchema } from '@platform/accesszen-contracts'

export const SNAPSHOT_CACHE_KEY = 'accesszen:snapshot'
// 5 minutes — short enough to expire before a typical session cookie, long
// enough to survive the multi-page navigations in a single settings flow.
export const SNAPSHOT_CACHE_TTL_MS = 5 * 60 * 1000

type CachedSnapshot = { data: PermissionSnapshot; ts: number }
export type CacheStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>

export function snapshotCacheKey(orgId: string): string {
  return `${SNAPSHOT_CACHE_KEY}:${orgId}`
}

/**
 * Pure decode of one cache entry. Null when absent, malformed, expired, invalid, or
 * written for a different organization. `orgId` is required: there is no unkeyed read,
 * so the cross-org check always applies.
 */
export function parseCachedSnapshot(
  raw: string | null,
  orgId: string,
  now: number,
): PermissionSnapshot | null {
  if (!raw) return null
  try {
    const entry = JSON.parse(raw) as CachedSnapshot
    if (now - entry.ts > SNAPSHOT_CACHE_TTL_MS) return null
    const result = PermissionSnapshotSchema.safeParse(entry.data)
    if (!result.success) return null
    if (result.data.org_id !== orgId) return null
    return result.data
  } catch {
    return null
  }
}

function defaultStorage(): CacheStorage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    // Access itself can throw (blocked storage) — the cache is best-effort.
    return null
  }
}

export function readSnapshotCache(
  orgId: string,
  storage: CacheStorage | null = defaultStorage(),
): PermissionSnapshot | null {
  // An empty id cannot name a cache entry: no cache beats a shared one.
  if (!storage || !orgId) return null
  try {
    const key = snapshotCacheKey(orgId)
    const snapshot = parseCachedSnapshot(storage.getItem(key), orgId, Date.now())
    if (snapshot === null) storage.removeItem(key)
    return snapshot
  } catch {
    return null
  }
}

export function writeSnapshotCache(
  snapshot: PermissionSnapshot,
  orgId: string,
  storage: CacheStorage | null = defaultStorage(),
): void {
  if (!storage || !orgId) return
  // A snapshot the server built for another org must not land under this org's key.
  if (snapshot.org_id !== orgId) return
  try {
    const entry: CachedSnapshot = { data: snapshot, ts: Date.now() }
    storage.setItem(snapshotCacheKey(orgId), JSON.stringify(entry))
  } catch {
    // Quota exceeded or blocked — fail silently, cache is best-effort
  }
}

/** Remove every org's entry (and the legacy key). Called on sign-out. */
export function clearSnapshotCache(storage: CacheStorage | null = defaultStorage()): void {
  if (!storage) return
  try {
    const keys: string[] = []
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (
        key !== null &&
        (key === SNAPSHOT_CACHE_KEY || key.startsWith(`${SNAPSHOT_CACHE_KEY}:`))
      ) {
        keys.push(key)
      }
    }
    keys.forEach((key) => storage.removeItem(key))
  } catch {
    // ignore
  }
}
